import React from 'react';
import { StatusCapsule } from './StatusCapsule';
import type { StatusCapsuleItem } from './StatusCapsule';

export interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  statusItems?: StatusCapsuleItem[];
  /** Right-aligned actions/controls in the header row */
  actions?: React.ReactNode;
}

/**
 * PageHeader — Unified header for all module pages.
 * Title 15px · Subtitle 11px · StatusCapsule strip.
 * Inherits horizontal padding from parent container.
 */
export function PageHeader({ title, subtitle, icon, statusItems, actions }: PageHeaderProps) {
  return (
    <div className={`page-header shrink-0 ${title ? 'border-b border-[var(--border-default)]' : ''}`} style={{ paddingTop: title ? 6 : 2, paddingBottom: title ? 5 : 2 }}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 shrink-0">
          {/* Title row */}
          <div className="flex items-center gap-1.5">
            {icon && <span className="enterprise-text-muted">{icon}</span>}
            <h1 className="truncate font-semibold enterprise-text-primary" style={{ fontSize: 15, lineHeight: '20px', letterSpacing: '-0.01em' }}>
              {title}
            </h1>
          </div>

          {/* Subtitle */}
          {subtitle && (
            <p className="enterprise-text-secondary" style={{ fontSize: 11, marginTop: 1 }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Right side: status capsules + actions */}
        <div className="flex items-center gap-2 ml-auto shrink-0 flex-wrap justify-end">
          {statusItems && statusItems.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {statusItems.map((item) => (
                <StatusCapsule key={item.label} {...item} />
              ))}
            </div>
          )}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageHeader;
